import React, { useState, useEffect, useRef } from 'react';
import { X, Fingerprint, CheckCircle, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import fingerprintApi from '../services/fingerprintApi';
import toast from 'react-hot-toast';

interface Employee {
  id: number;
  user: {
    name: string;
    email: string;
  };
}

interface FingerprintEnrollmentModalProps {
  employee: Employee;
  onClose: () => void;
}

interface FingerprintDevice {
  id: number;
  deviceId: number;
  name: string;
  status: 'active' | 'inactive';
  piWebUrl?: string;
}

const FingerprintEnrollmentModal: React.FC<FingerprintEnrollmentModalProps> = ({
  employee,
  onClose
}) => {
  const [step, setStep] = useState<'select' | 'opening' | 'success' | 'error'>('select');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [fingerprintId, setFingerprintId] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<FingerprintDevice | null>(null);
  const openedWindowRef = useRef<Window | null>(null);

  // Get available devices for current admin's tenant
  const { data: devicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: true
  });

  const devices: FingerprintDevice[] = devicesData?.devices || [];

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDevice) {
      const firstActiveDevice = devices.find(d => d.status === 'active') || devices[0];
      if (firstActiveDevice) {
        setSelectedDevice(firstActiveDevice);
      }
    }
  }, [devices, selectedDevice]);

  // Listen for postMessage from Pi UI
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: In production, validate event.origin
      // For now, accept messages from any origin (since Pi URL may vary)
      console.log('[ENROLLMENT MODAL] Received message:', event.data);

      const { type, ...data } = event.data;

      switch (type) {
        case 'enrollment-complete':
          setStep('success');
          setFingerprintId(data.fingerprintId);
          setStatusMessage('Fingerprint enrolled successfully!');
          toast.success('Fingerprint enrolled successfully');
          break;

        case 'enrollment-error':
          setStep('error');
          setErrorMessage(data.message || 'Enrollment failed');
          toast.error(data.message || 'Enrollment failed');
          break;

        case 'status-update':
          setStatusMessage(data.message || '');
          break;

        default:
          console.log('[ENROLLMENT MODAL] Unknown message type:', type);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleStartEnrollment = () => {
    if (!selectedDevice) {
      toast.error('Please select a device first');
      return;
    }

    if (!selectedDevice.piWebUrl) {
      toast.error('Pi web URL not configured for this device');
      return;
    }

    // Build Pi UI URL with parameters
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://textile-api.vuenexa.com';
    const url = new URL('/ui', selectedDevice.piWebUrl);
    url.searchParams.set('employeeId', employee.id.toString());
    url.searchParams.set('deviceId', selectedDevice.deviceId.toString());
    url.searchParams.set('mode', 'enroll');
    url.searchParams.set('backendUrl', backendUrl);

    // Open Pi UI in new window instead of iframe
    const newWindow = window.open(url.toString(), 'fingerprint-enrollment', 'width=800,height=600,resizable=yes,scrollbars=yes');
    
    if (!newWindow) {
      toast.error('Please allow popups to open the enrollment interface');
      setStep('error');
      setErrorMessage('Failed to open enrollment window. Please allow popups.');
      return;
    }

    openedWindowRef.current = newWindow;
    setStep('opening');
    setErrorMessage('');
    setStatusMessage('Opening Pi enrollment interface in new window...');
  };

  const handleClose = () => {
    // Close opened window if still open
    if (openedWindowRef.current && !openedWindowRef.current.closed) {
      openedWindowRef.current.close();
    }
    openedWindowRef.current = null;
    setStatusMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Enroll Fingerprint</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Employee: <span className="font-medium">{employee.user.name}</span> ({employee.user.email})
          </p>
        </div>

        {step === 'select' && (
          <div className="text-center py-8">
            <Fingerprint className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Select Device</h4>
            
            <div className="mb-6 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fingerprint Device:
              </label>
              <select
                value={selectedDevice?.deviceId || ''}
                onChange={(e) => {
                  const device = devices.find(d => d.deviceId === parseInt(e.target.value));
                  setSelectedDevice(device || null);
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">-- Select Device --</option>
                {devices.map((device) => (
                  <option key={device.id} value={device.deviceId}>
                    {device.name} (ID: {device.deviceId}) {device.status === 'active' ? '🟢' : '🔴'}
                    {device.piWebUrl ? ' 📡' : ' ⚠️'}
                  </option>
                ))}
              </select>
              {devices.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">No devices available. Please contact administrator.</p>
              )}
              {selectedDevice && !selectedDevice.piWebUrl && (
                <p className="mt-2 text-xs text-yellow-600">
                  ⚠️ Pi web URL not configured for this device. Please configure it in device settings.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={handleStartEnrollment}
                disabled={!selectedDevice || !selectedDevice.piWebUrl}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Enrollment
              </button>
            </div>
          </div>
        )}

        {step === 'opening' && (
          <div className="py-8 text-center">
            <div className="mb-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
            </div>
            {statusMessage && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">{statusMessage}</p>
              </div>
            )}
            <p className="text-sm text-gray-600 mb-4">
              The enrollment interface has been opened in a new window.
              <br />
              Please complete the enrollment there. This window will update automatically when enrollment is complete.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setStep('select');
                  if (openedWindowRef.current && !openedWindowRef.current.closed) {
                    openedWindowRef.current.focus();
                  }
                }}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Back
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Enrollment Successful</h4>
            <p className="text-sm text-gray-600 mb-6">
              Fingerprint has been enrolled successfully.
              {fingerprintId && (
                <span className="block mt-2">Fingerprint ID: {fingerprintId}</span>
              )}
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center py-8">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Enrollment Failed</h4>
            <p className="text-sm text-red-600 mb-6">{errorMessage}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setStep('select');
                  setErrorMessage('');
                }}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FingerprintEnrollmentModal;
